import { useEffect, useState } from 'react';

import type { GeoPoint } from '../types';

export type GeocodedAddressProps = {
  geoPoint?: GeoPoint | null;
  hideEmptyPlaceholder?: boolean;
  reverseGeocode?: (geoPoint: GeoPoint) => Promise<string>;
};

function angleToDegree(degrees: number, directionChar: string): string {
  const minutes = (degrees % 1) * 60;
  const seconds = (minutes % 1) * 60;
  return `${Math.floor(degrees)}° ${Math.floor(minutes)}' ${Math.floor(seconds)}"${directionChar}`;
}

export function formatToDegree(geoPoint: GeoPoint): string {
  const latitude = angleToDegree(geoPoint.latitude, geoPoint.latitude < 0 ? 'S' : 'N');
  const longitude = angleToDegree(geoPoint.longitude, geoPoint.longitude < 0 ? 'W' : 'E');
  return `${latitude} ${longitude}`;
}

export function GeocodedAddress({
  geoPoint,
  hideEmptyPlaceholder,
  reverseGeocode,
}: GeocodedAddressProps) {
  const [address, setAddress] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const geoPointKey = geoPoint != null ? `${geoPoint.latitude};${geoPoint.longitude}` : null;

  useEffect(() => {
    if (geoPoint == null || geoPointKey == null) {
      setAddress(null);
      return;
    }

    let cancelled = false;
    if (reverseGeocode == null) {
      setAddress(formatToDegree(geoPoint));
      return;
    }

    setIsLoading(true);
    reverseGeocode(geoPoint)
      .then((resolved) => {
        if (!cancelled) {
          setAddress(resolved);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAddress(formatToDegree(geoPoint));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoPointKey, reverseGeocode]);

  if (isLoading) {
    return (
      <span className="placeholder-glow">
        <span className="placeholder small">Loading address</span>
      </span>
    );
  }

  const effectiveValue = address ?? (hideEmptyPlaceholder ? '' : '–');
  return <span className="small">{effectiveValue}</span>;
}
